/**
 * テキストの行数を自動制限する機能
 *
 * 仕様
 * - ist-cardSmall__contentInner: 親要素
 * - ist-cardSmall__titleText: タイトル
 * - ist-cardSmall__mainText: 本文
 *
 * 使い方
 * <div class="ist-cardSmall__contentInner">
 *   <div class="ist-cardSmall__titleText">タイトル</div>
 *   <div class="ist-cardSmall__mainText">本文</div>
 * </div>
 */

export class CardSmallLineClamp {
  /** 要素のクラス名を定義 */
  private static readonly CLASSES = {
    CONTENT_INNER: 'ist-cardSmall__contentInner',
    TITLE_TEXT: 'ist-cardSmall__titleText',
    MAIN_TEXT: 'ist-cardSmall__mainText',
  };

  /** リサイズ監視用のオブザーバー */
  private resizeObserver: ResizeObserver;
  /** リサイズ処理のタイムアウトID */
  private resizeTimeout: number | null = null;
  /** 非表示用のクラス名 */
  private readonly NO_DISPLAY_CLASS = 'ist-js__cardSmallTextNodisplay';
  /** デバッグモードのフラグ */
  private debug: boolean;

  /**
   * CardSmallLineClampのインスタンスを作成
   * @param options デバッグモードなどのオプション設定
   */
  constructor(options: { debug?: boolean } = {}) {
    this.debug = options.debug || false;
    this.resizeObserver = new ResizeObserver(this.handleResize.bind(this));

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initialize());
    } else {
      this.initialize();
    }
  }

  /**
   * 初期化処理を実行
   * ResizeObserverの設定と初回の行数制限を適用
   */
  private initialize(): void {
    const contentInners = Array.from(
      document.getElementsByClassName(CardSmallLineClamp.CLASSES.CONTENT_INNER)
    ) as HTMLElement[];

    contentInners.forEach((contentInner) => {
      this.resizeObserver.observe(contentInner);
    });

    this.updateAllLineClamps();
  }

  /**
   * 要素の実際の高さを取得
   * @param element 測定対象の要素
   * @returns 要素の実際の高さ（ピクセル）
   */
  private getActualHeight(element: HTMLElement): number {
    const clone = element.cloneNode(true) as HTMLElement;
    const parent = element.parentElement;

    if (!parent) return 0;

    // クローン要素のスタイルを設定
    clone.style.cssText = `
      position: absolute !important;
      visibility: hidden !important;
      display: block !important;
      width: ${element.offsetWidth}px !important;
      height: auto !important;
      margin: 0 !important;
      padding: ${window.getComputedStyle(element).padding} !important;
    `;

    parent.appendChild(clone);
    const height = clone.scrollHeight;
    parent.removeChild(clone);

    this.log('Actual height measurement:', {
      element: element.className,
      height,
      content: element.textContent?.slice(0, 50) + '...',
    });

    return height;
  }

  /**
   * 要素の行の高さを取得
   * @param element 測定対象の要素
   * @returns 行の高さ（ピクセル）
   */
  private getLineHeight(element: HTMLElement): number {
    const style = window.getComputedStyle(element);
    let lineHeight = parseFloat(style.lineHeight);

    // line-heightが設定されていない場合はfont-sizeから計算
    if (isNaN(lineHeight)) {
      const fontSize = parseFloat(style.fontSize);
      lineHeight = fontSize * 1.2;
    }

    this.log('Line height:', lineHeight);
    return lineHeight;
  }

  /**
   * 要素のスタイルをリセット
   * @param contentInner リセット対象の親要素
   */
  private resetStyles(contentInner: HTMLElement): void {
    const titleText = contentInner.querySelector(
      `.${CardSmallLineClamp.CLASSES.TITLE_TEXT}`
    ) as HTMLElement;
    const mainText = contentInner.querySelector(
      `.${CardSmallLineClamp.CLASSES.MAIN_TEXT}`
    ) as HTMLElement;

    // タイトルのスタイルをリセット
    if (titleText) {
      titleText.style.removeProperty('--line-clamp');
      titleText.classList.remove(this.NO_DISPLAY_CLASS);
    }
    // 本文のスタイルをリセット
    if (mainText) {
      mainText.style.removeProperty('--line-clamp');
      mainText.classList.remove(this.NO_DISPLAY_CLASS);
    }
  }

  /**
   * タイトルと本文の行数を計算して制限を適用
   * @param contentInner 計算対象の親要素
   */
  private calculateLineClamp(contentInner: HTMLElement): void {
    // コンテナの高さを取得
    const contentInnerHeight = contentInner.offsetHeight;
    this.log('Content inner height:', contentInnerHeight);

    // タイトル要素を取得
    const titleText = contentInner.querySelector(
      `.${CardSmallLineClamp.CLASSES.TITLE_TEXT}`
    ) as HTMLElement;
    if (!titleText) return;

    // タイトルの行高さと実際の高さを計算
    const titleTextLineHeight = this.getLineHeight(titleText);
    const actualTitleHeight = this.getActualHeight(titleText);

    // タイトルが1行を超える場合は行数制限を適用
    if (actualTitleHeight > titleTextLineHeight) {
      const titleMaxLines = Math.floor(
        contentInnerHeight / titleTextLineHeight
      );
      if (titleMaxLines <= 0) {
        titleText.classList.add(this.NO_DISPLAY_CLASS);
      } else {
        titleText.style.setProperty('--line-clamp', titleMaxLines.toString());
      }
    }

    // 本文要素を取得
    const mainText = contentInner.querySelector(
      `.${CardSmallLineClamp.CLASSES.MAIN_TEXT}`
    ) as HTMLElement;
    if (!mainText) return;

    // マージンとパディングの計算
    const titleStyle = window.getComputedStyle(titleText);
    const titleMarginBottom = parseFloat(titleStyle.marginBottom);
    const mainStyle = window.getComputedStyle(mainText);
    const mainMarginTop = parseFloat(mainStyle.marginTop);
    const mainPaddingTop = parseFloat(mainStyle.paddingTop);
    const mainTextLineHeight = this.getLineHeight(mainText);

    // 本文に利用可能な高さを計算
    const availableMainHeight =
      contentInnerHeight -
      actualTitleHeight -
      titleMarginBottom -
      mainMarginTop -
      mainPaddingTop;
    const actualMainHeight = this.getActualHeight(mainText);

    this.log('Space calculation:', {
      contentInnerHeight,
      titleTextLineHeight,
      availableMainHeight,
      actualMainHeight,
    });

    // 本文の表示領域がない場合
    if (availableMainHeight <= 0) {
      mainText.classList.add(this.NO_DISPLAY_CLASS);
      return;
    }

    // 本文が利用可能な高さを超える場合は行数制限を適用
    if (actualMainHeight > availableMainHeight) {
      const mainMaxLines = Math.floor(availableMainHeight / mainTextLineHeight);
      if (mainMaxLines <= 0) {
        mainText.classList.add(this.NO_DISPLAY_CLASS);
      } else {
        mainText.style.setProperty('--line-clamp', mainMaxLines.toString());
        mainText.classList.remove(this.NO_DISPLAY_CLASS);
      }
    } else {
      mainText.classList.remove(this.NO_DISPLAY_CLASS);
    }
  }

  /**
   * リサイズイベントのハンドラー
   * @param entries リサイズされた要素のエントリー配列
   */
  private handleResize(entries: ResizeObserverEntry[]): void {
    // 既存のタイムアウトをキャンセル
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }

    // 新しいフレームでの処理をスケジュール
    this.resizeTimeout = window.requestAnimationFrame(() => {
      entries.forEach((entry) => {
        const contentInner = entry.target as HTMLElement;
        this.resetStyles(contentInner);
        this.calculateLineClamp(contentInner);
      });
      this.resizeTimeout = null;
    });
  }

  /**
   * すべての要素の行数制限を更新
   */
  private updateAllLineClamps(): void {
    const contentInners = Array.from(
      document.getElementsByClassName(CardSmallLineClamp.CLASSES.CONTENT_INNER)
    ) as HTMLElement[];

    contentInners.forEach((contentInner) => {
      this.resetStyles(contentInner);
      this.calculateLineClamp(contentInner);
    });
  }

  /**
   * デバッグログを出力
   * @param args ログ出力する引数
   */
  private log(...args: any[]): void {
    if (this.debug) {
      console.log('[LineClamp]', ...args);
    }
  }

  /**
   * インスタンスを破棄し、リソースを解放
   */
  public destroy(): void {
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }
    this.resizeObserver.disconnect();
  }

  /**
   * すべての要素の行数制限を再計算
   */
  public recalculate(): void {
    this.updateAllLineClamps();
  }
}
